import { NgIf, NgFor, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, Inject, OnDestroy } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { RootState } from '@next/core-lib/app';
import {
  FilterConfig,
  FilterOption,
  FiltersConfig,
  FilterTypes,
  SummaryCardAction,
  SummaryCardHorizontalItem,
  PageToolbarComponent,
  FilterComponent,
  SummaryCardHorizontalComponent,
  NotificationEmptyComponent,
} from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { DataSeries, DataSeriesParameter } from '@next/core-lib/models';
import { BreakpointServiceInjector, BreakpointService } from '@next/core-lib/responsive';
import { SettingsService, SettingsStorage } from '@next/core-lib/settings';
import { Store } from '@ngrx/store';
import { forkJoin, Observable, of, ReplaySubject, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  skipWhile,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { Cycle, Section } from 'src/app/cycle/models/cycle.model';
import { getCycles } from 'src/app/cycle/store/selectors/cycle.selectors';
import {
  ReferenceValueSheet,
  ReferenceValueSheetDataSeries,
} from 'src/app/reference-value-sheet/models/reference-value-sheet.model';
import {
  getReferenceValueSheetDataSeries,
  getReferenceValueSheets,
} from 'src/app/reference-value-sheet/store/reference-value-sheet.selectors';

import {
  availableKpiParameters,
  DeselectedKpisKey,
  ObservationsRegistration,
  Parameter,
  SettingsPoultryNEXTRearingContextKey,
  WeeklyKpiDataResult,
} from '../../models/performance.model';
import {
  getAvailableSections,
  getAvailableWeeks,
  getDailyKpiTimeseries,
  getObservations,
  getSelectedCycle,
  getSelectedCycleId,
  getSelectedKpiParameters,
  getSelectedSectionIds,
  getSelectedSections,
  getSelectedWeekNr,
  getWeeklyKpiDataResults,
} from '../../store/selectors/performance.selector';
import { ObservationsRegistrationComponent } from '../observations-registration/observations-registration.component';
import { PerformanceKpiChartDialogComponent } from '../performance-kpi-chart-dialog/performance-kpi-chart-dialog.component';
import * as rvsActions from './../../../reference-value-sheet/store/reference-value-sheet.actions';
import * as performanceActions from './../../store/actions/performance.actions';

@Component({
  selector: 'app-performance-overview',
  templateUrl: 'performance-overview.component.html',
  styleUrls: ['./performance-overview.component.scss'],
  imports: [
    PageToolbarComponent,
    FilterComponent,
    NgIf,
    NgFor,
    SummaryCardHorizontalComponent,
    NgTemplateOutlet,
    NotificationEmptyComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class PerformanceOverviewComponent implements AfterViewInit, OnDestroy {
  public readonly cycles$: Observable<Cycle[]> = this.store.select(getCycles);
  public readonly observations$: Observable<ObservationsRegistration[]> =
    this.store.select(getObservations);
  public readonly selectedSections$: Observable<Section[]> = this.store.select(getSelectedSections);
  public readonly selectedCycle$: Observable<Cycle> = this.store.select(getSelectedCycle);
  public readonly hasArrivals$ = this.selectedCycle$.pipe(
    map((cycle) => cycle?.arrivals?.length > 0),
  );
  public readonly weeklyKpiDataResults$: Observable<WeeklyKpiDataResult[]> =
    this.store.select(getWeeklyKpiDataResults);
  public readonly selectedKpiParameters$: Observable<Parameter[]> =
    this.store.select(getSelectedKpiParameters);
  public readonly selectedWeekNr$: Observable<number> = this.store.select(getSelectedWeekNr);
  public readonly referenceValueSheets$: Observable<ReferenceValueSheet[]> =
    this.store.select(getReferenceValueSheets);

  private readonly destroyed$ = new Subject<void>();
  private readonly cycleOptions$: Observable<FilterOption<number>[]> = this.store
    .select(getCycles)
    .pipe(
      map((cycles) =>
        cycles?.map((c) => ({
          viewValue: c.cycleNumber.toString(),
          value: c.id,
        })),
      ),
    );
  private readonly selectedCycleId$: Observable<number> = this.store.select(getSelectedCycleId);
  private readonly availableSectionOptions$: Observable<FilterOption<number>[]> = this.store
    .select(getAvailableSections)
    .pipe(
      map((sections) =>
        sections?.map((s) => ({
          viewValue: `${s.name} (${s.barn.name})`,
          value: s.id,
        })),
      ),
    );
  private readonly selectedSectionIds$: Observable<number[]> =
    this.store.select(getSelectedSectionIds);
  private readonly availableWeeksOptions$: Observable<FilterOption<number>[]> = this.store
    .select(getAvailableWeeks)
    .pipe(
      map((weeks) =>
        weeks?.map((w) => ({
          viewValue: w.toString(),
          value: w,
        })),
      ),
    );

  private readonly dailyKpiTimeSeries$: Observable<DataSeries<number, number>> =
    this.store.select(getDailyKpiTimeseries);

  private readonly referenceValueSheetDataSeries$: Observable<ReferenceValueSheetDataSeries[]> =
    this.store.select(getReferenceValueSheetDataSeries);

  private readonly cycleChangeSubject$: Subject<number> = new Subject<number>();
  private readonly weekNrChangeSubject$: Subject<number> = new Subject<number>();
  private readonly sectionsChangeSubject$: Subject<number[]> = new ReplaySubject<number[]>(1);
  private readonly kpiParametersChangeSubject$: Subject<Parameter[]> = new ReplaySubject<
    Parameter[]
  >(1);

  private selectedKpi: Parameter;

  filterConfig: FilterConfig<FiltersConfig> = {
    settings: false,
    altButton: false,
    filters: {
      cycle: {
        type: FilterTypes.Select,
        label: this.locale.translate('performance.cycle'),
        options: this.cycleOptions$,
        value: this.selectedCycleId$,
        changes: this.cycleChangeSubject$,
        priority: true,
      },
      weekNr: {
        type: FilterTypes.Select,
        label: this.locale.translate('performance.weekNr'),
        options: this.availableWeeksOptions$,
        value: this.selectedWeekNr$,
        changes: this.weekNrChangeSubject$,
        priority: true,
      },
      sections: {
        type: FilterTypes.MultiSelect,
        label: this.locale.translate('performance.sections'),
        options: this.availableSectionOptions$,
        value: this.selectedSectionIds$,
        changes: this.sectionsChangeSubject$,
        priority: true,
      },
      kpis: {
        type: FilterTypes.MultiSelect,
        label: this.locale.translate('performance.kpis'),
        options: of(
          availableKpiParameters.map(
            (p) =>
              ({
                viewValue: this.locale.translate(`performance.kpi.${p.toString()}`),
                value: p,
              }) as FilterOption<Parameter>,
          ),
        ),
        value: this.selectedKpiParameters$,
        changes: this.kpiParametersChangeSubject$,
        priority: true,
      },
    },
  };

  public cycleSections$(): Observable<Section[]> {
    return this.selectedCycle$.pipe(map((cycle) => cycle?.sections));
  }

  getSummaryCardActions$(section: Section): Observable<SummaryCardAction[]> {
    return this.cycleSections$().pipe(
      withLatestFrom(this.selectedCycle$),
      skipWhile(([sections, selectedCycle]) => !sections || !selectedCycle),
      take(1),
      map(([sections, selectedCycle]) => {
        const sectionsWithAllocations = selectedCycle.arrivals
          .flatMap((a) => a.allocations)
          .map((a) => a.section);
        const sectionsWithMovements = selectedCycle.movements.map((m) => m.toSection);
        const sectionsWithAllocationsOrMovements =
          sectionsWithAllocations.concat(sectionsWithMovements);

        if (
          sections.find((s) => s.id === section.id) &&
          sectionsWithAllocationsOrMovements.find((s) => s.id === section.id)
        ) {
          return [
            {
              id: 'edit-action',
              icon: 'edit',
              callback: () => {
                this.selectedCycle$
                  .pipe(withLatestFrom(this.selectedWeekNr$, this.observations$), take(1))
                  .subscribe(([cycle, weekNr, observations]) => {
                    this.dialogService.fullscreenDialog(ObservationsRegistrationComponent, {
                      selectedSection: section,
                      sections,
                      cycle,
                      observations,
                      weekNr,
                      confirmButton: true,
                      confirmButtonText: `${this.locale.translate('save')}`,
                      altButton: sections?.length > 1,
                      altButtonText: this.locale.translate(
                        'observations-labels.save-and-next-section',
                      ),
                      pushHistoryState: false,
                    });
                  });
              },
            },
          ];
        } else {
          return [];
        }
      }),
    );
  }

  constructor(
    private readonly store: Store<RootState>,
    private readonly locale: LocaleService,
    private readonly dialogService: DialogService,
    @Inject(BreakpointServiceInjector) private readonly breakpoint: BreakpointService,
    @Inject(SettingsService) private readonly settingsService: SettingsService,
  ) {
    this.cycles$
      .pipe(
        withLatestFrom(
          this.settingsService.getSingle<Parameter[]>(
            {
              contextName: SettingsPoultryNEXTRearingContextKey,
              key: DeselectedKpisKey,
              basedOnCurrentCustomer: true,
              basedOnCurrentProfile: true,
              storage: SettingsStorage.AppLocal,
            },
            [],
          ),
        ),
        skipWhile(([cycles, _]) => !cycles || cycles.length === 0),
        take(1),
      )
      .subscribe(([cycles, deselectedParams]) => {
        if (cycles.length > 0) {
          const sorted = cycles.sort((a, b) => (a.cycleNumber < b.cycleNumber ? 1 : -1));
          this.store.dispatch(performanceActions.setSelectedCycleId({ cycleId: sorted[0].id }));
          this.store.dispatch(
            performanceActions.setSelectedKpiParameters({
              kpiParameters: this.getInverseParameterSelection(deselectedParams),
            }),
          );
        }
      });
  }
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    this.cycleChangeSubject$
      .pipe(takeUntil(this.destroyed$), this.distinctUntilChangedObj())
      .subscribe((cycleId) =>
        this.store.dispatch(performanceActions.setSelectedCycleId({ cycleId })),
      );

    this.weekNrChangeSubject$
      .pipe(takeUntil(this.destroyed$), this.distinctUntilChangedObj())
      .subscribe((weekNr) => this.store.dispatch(performanceActions.setSelectedWeekNr({ weekNr })));

    this.sectionsChangeSubject$
      .pipe(takeUntil(this.destroyed$), this.distinctUntilChangedObj())
      .subscribe((sectionIds) => {
        this.store.dispatch(performanceActions.setSelectedSectionIds({ sectionIds }));
      });

    this.kpiParametersChangeSubject$
      .pipe(takeUntil(this.destroyed$), this.distinctUntilChangedObj())
      .subscribe((kpiParameters) => {
        this.store.dispatch(performanceActions.setSelectedKpiParameters({ kpiParameters }));
        this.settingsService
          .setSettings({
            contextName: SettingsPoultryNEXTRearingContextKey,
            key: DeselectedKpisKey,
            basedOnCurrentCustomer: true,
            basedOnCurrentProfile: true,
            storage: SettingsStorage.AppLocal,
            data: this.getInverseParameterSelection(kpiParameters),
          })
          .subscribe();
      });
  }

  changeCycleSelection(selection: MatSelectChange) {
    this.dispatchSelectedCycleId(selection.value as number);
  }

  dispatchSelectedCycleId(cycleId: number) {
    this.store.dispatch(performanceActions.setSelectedCycleId({ cycleId }));
  }

  openAddWeekDialog() {
    this.store
      .select(getAvailableWeeks)
      .pipe(take(1))
      .subscribe((availableWeeks) => {
        const week = availableWeeks?.length > 0 ? Math.max(...availableWeeks) + 1 : 1;
        this.selectedCycle$
          .pipe(
            withLatestFrom(this.observations$, this.hasArrivals$, this.cycleSections$()),
            take(1),
          )
          .subscribe(([cycle, observations, hasArrivals, sections]) => {
            if (hasArrivals) {
              this.dialogService.fullscreenDialog(ObservationsRegistrationComponent, {
                selectedSection: undefined,
                sections,
                cycle,
                observations,
                weekNr: week,
                confirmButton: true,
                confirmButtonText: `${this.locale.translate('save')}`,
                altButton: sections?.length > 1,
                altButtonText: this.locale.translate('observations-labels.save-and-next-section'),
                pushHistoryState: false,
              });
            }
          });
      });
  }

  resetFilter() {
    this.availableSectionOptions$.pipe(take(1)).subscribe((sections) => {
      this.sectionsChangeSubject$.next(sections.map((s) => s.value));
      this.kpiParametersChangeSubject$.next(availableKpiParameters);
    });
  }

  distinctUntilChangedObj<T>() {
    return distinctUntilChanged<T>((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  getSummaryCardItems(
    sectionId: number,
    results: WeeklyKpiDataResult[],
    weekNr: number,
    observations: ObservationsRegistration[],
    selectedKpis: Parameter[],
  ): SummaryCardHorizontalItem[] {
    const kpiItems: SummaryCardHorizontalItem[] = [];
    const sectionResult = results.find((r) => r.sectionId === sectionId && r.weekNr === weekNr);
    const filteredObservations = observations.filter(
      (o) => o.sectionId === sectionId && Math.floor(o.day / 7) + 1 === weekNr,
    );

    if (selectedKpis && sectionResult) {
      selectedKpis.forEach((kpi) => {
        if (kpi === Parameter.FeedName) {
          const feedNames = Array.from(
            new Set(filteredObservations.map((o) => o.feedName).filter((n) => n)),
          ).join(',');
          kpiItems.push({
            label: `performance.kpi.${kpi.toString()}`,
            value: feedNames.length > 0 ? feedNames : this.locale.translate('n/a'),
            unit: '',
            decimalPrecision: 0,
            prefixUnit: false,
          });
        } else if (kpi === Parameter.Notes) {
          const notes = filteredObservations
            .map((o) => o.notes)
            .filter((n) => n)
            .join(',');
          kpiItems.push({
            label: `performance.kpi.${kpi.toString()}`,
            value: notes.length > 0 ? notes : this.locale.translate('n/a'),
            unit: '',
            decimalPrecision: 0,
            prefixUnit: false,
          });
        } else {
          const kpiResult = sectionResult.data.find((d) => d.parameter.code === kpi.toString());
          if (
            (kpiResult && kpiResult?.value === undefined) ||
            isNaN(kpiResult?.value) ||
            !isFinite(kpiResult?.value)
          ) {
            kpiItems.push({
              label: `performance.kpi.${kpi.toString()}`,
              value: this.locale.translate('n/a'),
              unit: '',
              decimalPrecision: 0,
              prefixUnit: false,
            });
          } else {
            kpiItems.push({
              label: `performance.kpi.${kpi.toString()}`,
              value: kpiResult?.value,
              unit: kpiResult.parameter.unit ?? '',
              decimalPrecision: kpiResult.parameter.numberOfDecimals ?? 0,
              prefixUnit: false,
              valueAction: () => this.kpiClicked(kpiResult.parameter, sectionId),
            });
          }
        }
      });
    }

    return kpiItems;
  }

  kpiClicked(parameter: DataSeriesParameter, _sectionId: number): void {
    const kpi = parameter.code as unknown as Parameter;
    this.selectedKpi = kpi;

    this.selectedCycleId$
      .pipe(withLatestFrom(this.selectedSectionIds$, this.store.select(getCycles)), take(1))
      .subscribe(([cycleId, sectionIds, cycles]) => {
        this.store.dispatch(
          performanceActions.loadTimeSeriesRequest({
            cycleId,
            parameter: kpi,
            sectionIds,
            rvsIds: cycles
              .find((cycle) => cycle.id === cycleId)
              .sections.map((section: Section) => section.referenceValueSheetId),
          }),
        );

        forkJoin([
          this.dailyKpiTimeSeries$.pipe(
            skipWhile(
              (timeSeries) =>
                timeSeries === undefined ||
                !timeSeries.metaData.parameters.some((p) => p.code === kpi.toString()),
            ),
            take(1),
          ),
          this.referenceValueSheetDataSeries$.pipe(
            skipWhile(
              (referenceValueSheetDataSeries) => referenceValueSheetDataSeries === undefined,
            ),
            take(1),
          ),
          this.breakpoint.ltSm$.pipe(take(1)),
          this.selectedSections$.pipe(take(1)),
        ])
          .pipe(take(1))
          .subscribe(([timeSeries, referenceValueSheetDataSeries, isMobile, sections]) => {
            if (
              timeSeries &&
              this.selectedKpi &&
              sections.length > 0 &&
              referenceValueSheetDataSeries
            ) {
              this.dialogService
                .fullscreenDialog(
                  PerformanceKpiChartDialogComponent,
                  {
                    kpi: this.selectedKpi,
                    cycle: cycles.find((cycle) => cycle.id === cycleId),
                    sectionsDataSeries: timeSeries,
                    sections,
                    referenceValueSheetDataSeries,
                    pushHistoryState: false,
                  },
                  {
                    width: isMobile ? '100vw' : '50vw',
                  },
                )
                .subscribe((_) => {
                  this.store.dispatch(performanceActions.loadDailyKpiTimeSeriesSuccess(undefined));
                  this.store.dispatch(rvsActions.loadRefValSheetDataSeriesSuccess(undefined));
                });
            }
          });
      });
  }

  private getInverseParameterSelection(selection: Parameter[]): Parameter[] {
    return availableKpiParameters.filter((k) => selection.indexOf(k) < 0);
  }
}
